import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationStart, Router } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { ExtractionReportResponse } from '../../models/truth-management.model';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilities, ClientCapabilityService } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';

type TimeFilter = 'today' | 'last7' | 'last30' | 'custom';
const SHARED_DATE_FILTER_KEY = 'docsila.sharedDateFilter.operationsScope';

interface SharedDateFilterState {
  selectedTimeFilter: TimeFilter;
  startDate: string;
  endDate: string;
  startDateDisplay: string;
  endDateDisplay: string;
  updatedAt: number;
}

type SortColumn =
  | 'document_model'
  | 'docs_processed'
  | 'pages_processed'
  | 'execution_percent'
  | 'average_duration_seconds'
  | 'total_llm_cost'
  | 'total_ocr_cost'
  | 'failures';

interface ExtractionReportRow {
  document_model: string;
  docs_processed: number;
  pages_processed: number;
  failures: number;
  average_duration_seconds: number;
  avg_llm_cost: number;
  total_llm_cost: number;
  avg_ocr_cost: number;
  total_ocr_cost: number;
  execution_percent: number;
}

interface TopMetrics {
  totalRequests: number;
  totalPagesProcessed: number;
  totalLlmCost: number;
  totalOcrCost: number;
  successRate: number;
}

@Component({
  selector: 'app-operations-request-analytics',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCalendar, MatNativeDateModule, PageHeaderComponent],
  templateUrl: './operations-request-analytics.component.html',
  styleUrls: ['./operations-request-analytics.component.scss'],
})
export class OperationsRequestAnalyticsComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isRoleResolved = false;
  isLoading = false;

  searchTerm = '';
  selectedTimeFilter: TimeFilter = 'today';
  startDate = '';
  endDate = '';
  startDateDisplay = '';
  endDateDisplay = '';
  endDateInvalid = false;
  dateRangeValidationMessage = '';
  private hasShownInvalidDateRangeToast = false;
  startHour12 = '12';
  startMinute = '00';
  startPeriod: 'AM' | 'PM' = 'AM';
  endHour12 = '12';
  endMinute = '00';
  endPeriod: 'AM' | 'PM' = 'AM';
  readonly hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  readonly minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  activeDateTimePopup: 'start' | 'end' | null = null;
  popupDate: Date | null = null;
  popupHour12 = '12';
  popupMinute = '00';
  popupPeriod: 'AM' | 'PM' = 'AM';
  private popupDirty = false;

  sortColumn: SortColumn | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  allRows: ExtractionReportRow[] = [];
  displayedRows: ExtractionReportRow[] = [];

  readonly topMetrics: TopMetrics = {
    totalRequests: 0,
    totalPagesProcessed: 0,
    totalLlmCost: 0,
    totalOcrCost: 0,
    successRate: 0,
  };

  private readonly destroy$ = new Subject<void>();

  showContactAdminEmpty = false;
  private hasEmittedCapabilities = false;
  capabilities: ClientCapabilities | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    public router: Router,
    public authService: AuthService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private truthManagementService: TruthManagement,
    private clientCapability: ClientCapabilityService,
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (!this.isSharedDateFilterRoute(event.url)) {
          sessionStorage.removeItem(SHARED_DATE_FILTER_KEY);
        }
      });

    this.authService.userRole$
      .pipe(takeUntil(this.destroy$))
      .subscribe((role) => {
        if (role !== null) {
          this.isRoleResolved = true;
          this.cdr.detectChanges();
        }
      });

    // Initialize dates BEFORE setting up client subscriptions
    // so dates are ready when fetchReport() is called
    const sharedState = this.loadSharedDateFilterState();
    if (sharedState) {
      this.applySharedDateFilterState(sharedState);
    } else {
      const now = new Date();
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
      this.syncTimePartsFromDateTimes();
      this.syncDateDisplayValues();
      this.saveSharedDateFilterState();
    }

    // Subscribe to client initialization and changes - reload report on both
    this.authService.currentClient$
      .pipe(
        filter(client => client !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.fetchReport();
      });

    this.clientCapability.capabilities$
      .pipe(takeUntil(this.destroy$))
      .subscribe((caps) => {
        this.capabilities = caps;
        if (!caps) {
          this.showContactAdminEmpty = false;
          return;
        }
        this.showContactAdminEmpty = !caps.extractionEnabled;
        this.hasEmittedCapabilities = true;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToIndexing(): void {
    this.router.navigate(['/indexing']);
  }

  onIndexingToggleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.checked) {
      this.navigateToIndexing();
    }
  }

  onDocumentModelClick(documentModel: string): void {
    this.router.navigate(['/extraction-reports', encodeURIComponent(documentModel)]);
  }

  formatCostDisplay(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '-';
    }

    const rounded = numeric.toFixed(2);
    if (rounded === '0.00') {
      return '-';
    }

    return `$ ${rounded}`;
  }

  onTimeFilterChange(): void {
    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;

    if (this.selectedTimeFilter !== 'custom') {
      // Avoid triggering custom refresh while closing popup due to preset switch.
      this.popupDirty = false;
      this.activeDateTimePopup = null;
    }

    const now = new Date();
    if (this.selectedTimeFilter === 'today') {
      const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last7') {
      const start = new Date(now.getTime() - 168 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    } else if (this.selectedTimeFilter === 'last30') {
      const start = new Date(now.getTime() - 720 * 60 * 60 * 1000);
      this.startDate = this.formatDateTimeForInput(start);
      this.endDate = this.formatDateTimeForInput(now);
    }

    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.saveSharedDateFilterState();

    if (this.selectedTimeFilter !== 'custom') {
      this.fetchReport();
    }
  }

  onDateChange(): void {
    this.syncTimePartsFromDateTimes();
    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';

    if (this.validateDateRange()) {
      this.saveSharedDateFilterState();
      this.fetchReport();
    }
  }

  onDateInputChange(field: 'start' | 'end', event: Event): void {
    const input = event.target as HTMLInputElement;
    const formatted = this.normalizeDisplayDateTimeInput(input.value);

    if (field === 'start') {
      this.startDateDisplay = formatted;
    } else {
      this.endDateDisplay = formatted;
    }

    if (input.value !== formatted) {
      input.value = formatted;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
  }

  onDateInputKeydown(event: KeyboardEvent): void {
    const key = event.key;
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];

    if (allowedKeys.includes(key) || event.ctrlKey || event.metaKey) {
      return;
    }

    if (!/^[\d/:\s]$/.test(key) && !/^[aApPmM]$/.test(key)) {
      event.preventDefault();
    }
  }

  onDateInputBlur(field: 'start' | 'end'): void {
    const value = (field === 'start' ? this.startDateDisplay : this.endDateDisplay).trim();

    if (!value) {
      this.notificationService.showError('Invalid date picked. Please use MM/DD/YYYY hh:mm AM/PM format.');
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const localDateTime = this.convertDisplayToLocalDateTime(value);
    if (!localDateTime) {
      this.notificationService.showError('Invalid date picked. Please use a valid MM/DD/YYYY hh:mm AM/PM date and time.');
      this.endDateInvalid = field === 'end';
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    const currentValue = field === 'start' ? this.startDate : this.endDate;

    if (field === 'start') {
      this.startDateDisplay = this.formatDateForDisplay(localDateTime);
    } else {
      this.endDateDisplay = this.formatDateForDisplay(localDateTime);
    }

    if (localDateTime === currentValue) {
      return;
    }

    if (field === 'start') {
      this.startDate = localDateTime;
    } else {
      this.endDate = localDateTime;
    }

    this.onDateChange();
  }

  onDatePartChange(field: 'start' | 'end', datePart: string): void {
    if (!datePart) {
      return;
    }

    if (field === 'start') {
      this.startDate = this.buildLocalDateTime(datePart, this.startHour12, this.startMinute, this.startPeriod);
    } else {
      this.endDate = this.buildLocalDateTime(datePart, this.endHour12, this.endMinute, this.endPeriod);
    }

    this.onDateChange();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Handle date-time popup
    if (this.activeDateTimePopup) {
      const path = (event.composedPath?.() || []) as EventTarget[];
      const isInsidePopup = path.some((target) => {
        if (!(target instanceof HTMLElement)) {
          return false;
        }

        return (
          target.classList.contains('date-time-popup-anchor') ||
          target.classList.contains('date-time-popup-panel') ||
          target.classList.contains('mat-calendar')
        );
      });

      if (!isInsidePopup) {
        this.closeDateTimePopup();
      }
    }
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.closeDateTimePopup();
  }

  openDateTimePopup(field: 'start' | 'end', event?: Event): void {
    event?.stopPropagation();

    if (this.activeDateTimePopup && this.activeDateTimePopup !== field) {
      this.closeDateTimePopup();
    }

    this.activeDateTimePopup = field;
    this.popupDirty = false;

    const source = field === 'start' ? this.startDate : this.endDate;
    const parsed = this.parseLocalDateTime(source);

    if (parsed) {
      this.popupDate = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      const isPm = parsed.getHours() >= 12;
      this.popupPeriod = isPm ? 'PM' : 'AM';
      this.popupHour12 = String(parsed.getHours() % 12 || 12).padStart(2, '0');
      this.popupMinute = String(parsed.getMinutes()).padStart(2, '0');
      this.removeCalendarNavTitles();
      return;
    }

    const now = new Date();
    this.popupDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.popupHour12 = '12';
    this.popupMinute = '00';
    this.popupPeriod = 'AM';

    this.removeCalendarNavTitles();
  }

  onPopupPanelClick(event: MouseEvent): void {
    event.stopPropagation();
    this.removeCalendarNavTitles();
  }

  closeDateTimePopup(event?: Event): void {
    event?.stopPropagation();
    if (this.popupDirty) {
      this.onDateChange();
    }
    this.activeDateTimePopup = null;
    this.popupDirty = false;
  }

  onPopupDateSelected(date: Date | null): void {
    if (!date) {
      return;
    }

    this.popupDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    this.commitPopupSelection();
  }

  onPopupTimePartChange(): void {
    this.commitPopupSelection();
  }

  private commitPopupSelection(): void {
    if (!this.activeDateTimePopup || !this.popupDate) {
      return;
    }

    const datePart = this.formatDatePartFromDate(this.popupDate);
    const localDateTime = this.buildLocalDateTime(datePart, this.popupHour12, this.popupMinute, this.popupPeriod);
    const currentValue = this.activeDateTimePopup === 'start' ? this.startDate : this.endDate;

    if (localDateTime === currentValue) {
      this.syncDateDisplayValues();
      this.updateEndDateInvalidState();
      return;
    }

    if (this.activeDateTimePopup === 'start') {
      this.startDate = localDateTime;
      this.startHour12 = this.popupHour12;
      this.startMinute = this.popupMinute;
      this.startPeriod = this.popupPeriod;
    } else {
      this.endDate = localDateTime;
      this.endHour12 = this.popupHour12;
      this.endMinute = this.popupMinute;
      this.endPeriod = this.popupPeriod;
    }

    this.syncDateDisplayValues();
    this.selectedTimeFilter = 'custom';
    this.popupDirty = true;
    this.updateEndDateInvalidState();
  }

  private removeCalendarNavTitles(): void {
    setTimeout(() => {
      const navButtons = document.querySelectorAll(
        '.date-time-popup-panel .mat-calendar-previous-button, .date-time-popup-panel .mat-calendar-next-button'
      );

      navButtons.forEach((button) => {
        if (button instanceof HTMLElement) {
          button.removeAttribute('title');
        }
      });
    }, 0);
  }

  getPopupMinDate(field: 'start' | 'end'): Date | null {
    if (field === 'start') {
      return null;
    }

    const start = this.parseLocalDateTime(this.startDate);
    return start ? new Date(start.getFullYear(), start.getMonth(), start.getDate()) : null;
  }

  getPopupMaxDate(field: 'start' | 'end'): Date | null {
    if (field === 'end') {
      return null;
    }

    return this.getTodayDateForPopup();
  }

  getTodayDateForPopup(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  isPopupOpen(field: 'start' | 'end'): boolean {
    return this.activeDateTimePopup === field;
  }

  onTimePartChange(field: 'start' | 'end'): void {
    const datePart = this.getDatePart(field === 'start' ? this.startDate : this.endDate);
    if (!datePart) {
      return;
    }

    if (field === 'start') {
      this.startDate = this.buildLocalDateTime(datePart, this.startHour12, this.startMinute, this.startPeriod);
    } else {
      this.endDate = this.buildLocalDateTime(datePart, this.endHour12, this.endMinute, this.endPeriod);
    }

    this.onDateChange();
  }

  formatDateForDisplay(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    const date = this.parseLocalDateTime(dateValue);
    if (!date) {
      return '';
    }

    return this.formatDateTimeForDisplay(date);
  }

  private syncDateDisplayValues(): void {
    this.startDateDisplay = this.formatDateForDisplay(this.startDate);
    this.endDateDisplay = this.formatDateForDisplay(this.endDate);
  }

  private normalizeDisplayDateTimeInput(value: string): string {
    return value.replace(/\s+/g, ' ').toUpperCase();
  }

  private loadSharedDateFilterState(): SharedDateFilterState | null {
    const raw = sessionStorage.getItem(SHARED_DATE_FILTER_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<SharedDateFilterState>;
      const selected = parsed.selectedTimeFilter;
      if (!selected || !['today', 'last7', 'last30', 'custom'].includes(selected)) {
        return null;
      }

      const start = typeof parsed.startDate === 'string' ? parsed.startDate : '';
      const end = typeof parsed.endDate === 'string' ? parsed.endDate : '';
      if (!this.parseLocalDateTime(start) || !this.parseLocalDateTime(end)) {
        return null;
      }

      return {
        selectedTimeFilter: selected as TimeFilter,
        startDate: start,
        endDate: end,
        startDateDisplay: typeof parsed.startDateDisplay === 'string' ? parsed.startDateDisplay : this.formatDateForDisplay(start),
        endDateDisplay: typeof parsed.endDateDisplay === 'string' ? parsed.endDateDisplay : this.formatDateForDisplay(end),
        updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
      };
    } catch {
      return null;
    }
  }

  private applySharedDateFilterState(state: SharedDateFilterState): void {
    this.selectedTimeFilter = state.selectedTimeFilter;
    this.startDate = state.startDate;
    this.endDate = state.endDate;
    this.startDateDisplay = state.startDateDisplay || this.formatDateForDisplay(state.startDate);
    this.endDateDisplay = state.endDateDisplay || this.formatDateForDisplay(state.endDate);
    this.syncTimePartsFromDateTimes();
    this.updateEndDateInvalidState();
  }

  private saveSharedDateFilterState(): void {
    const payload: SharedDateFilterState = {
      selectedTimeFilter: this.selectedTimeFilter,
      startDate: this.startDate,
      endDate: this.endDate,
      startDateDisplay: this.startDateDisplay,
      endDateDisplay: this.endDateDisplay,
      updatedAt: Date.now(),
    };

    sessionStorage.setItem(SHARED_DATE_FILTER_KEY, JSON.stringify(payload));
  }

  private isSharedDateFilterRoute(url: string): boolean {
    const path = (url || '').split('?')[0].split('#')[0];
    return (
      path === '/indexing' ||
      path.startsWith('/operations-request-analytics') ||
      path === '/extraction-reports' ||
      path.startsWith('/extraction-reports/') ||
      path === '/source'
    );
  }

  private convertDisplayToLocalDateTime(value: string): string | null {
    const trimmed = value.trim().toUpperCase();
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/);
    if (!match) {
      return null;
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    if (month < 1 || month > 12 || year < 1900 || year > 2100) {
      return null;
    }

    let hours = 0;
    let minutes = 0;
    if (match[4] !== undefined && match[5] !== undefined && match[6] !== undefined) {
      const hour12 = Number(match[4]);
      minutes = Number(match[5]);
      const period = match[6];

      if (hour12 < 1 || hour12 > 12 || minutes < 0 || minutes > 59) {
        return null;
      }

      hours = period === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
    }

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return this.formatDateTimeForInput(date);
  }

  getDatePart(localDateTime: string): string {
    return localDateTime?.split('T')[0] || '';
  }

  onSearchChange(): void {
    this.applyLocalFiltersAndSort();
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applyLocalFiltersAndSort();
  }

  getSortIcon(column: SortColumn): SafeHtml {
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>&#9650;</span><span>&#9660;</span></div>');
    }

    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '&#9650;' : '&#9660;');
  }

  private fetchReport(): void {
    this.isLoading = true;

    if (this.selectedTimeFilter === 'custom' && !this.validateDateRange()) {
      this.isLoading = false;
      return;
    }

    const startDateParam = this.toUtcPayloadValue(this.startDate);
    const endDateParam = this.toUtcPayloadValue(this.endDate);

    // Get current clientId from AuthService
    const currentClient = this.authService.getCurrentClient();
    const clientId = currentClient?.client_id;
    this.truthManagementService
      .getExtractionReport(startDateParam, endDateParam, clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const rows = response?.by_document_model || [];

          this.allRows = rows.map((item) => {
            const total = Number(item.docs_processed) || 0;
            const pagesProcessed = Number(item.pages_processed) || 0;
            const failures = Number(item.failures) || 0;
            const avgLlmCost = Number(item.average_llm_cost) || 0;
            const avgOcrCost = Number(item.average_ocr_cost) || 0;
            const executed = total > 0 ? ((total - failures) / total) * 100 : 0;

            return {
              document_model: item.document_model,
              docs_processed: total,
              pages_processed: pagesProcessed,
              failures,
              average_duration_seconds: Number(item.average_duration_seconds) || 0,
              avg_llm_cost: avgLlmCost,
              total_llm_cost: avgLlmCost * total,
              avg_ocr_cost: avgOcrCost,
              total_ocr_cost: avgOcrCost * total,
              execution_percent: Math.max(0, Math.min(100, executed)),
            };
          });

          this.updateTopMetrics();
          this.applyLocalFiltersAndSort();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.allRows = [];
          this.displayedRows = [];
          this.updateTopMetrics();
          this.isLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private updateTopMetrics(): void {
    const totalRequests = this.allRows.reduce((sum, row) => sum + row.docs_processed, 0);
    const totalFailures = this.allRows.reduce((sum, row) => sum + row.failures, 0);

    this.topMetrics.totalRequests = totalRequests;
    this.topMetrics.totalPagesProcessed = this.allRows.reduce((sum, row) => sum + row.pages_processed, 0);
    this.topMetrics.totalLlmCost = this.allRows.reduce((sum, row) => sum + row.total_llm_cost, 0);
    this.topMetrics.totalOcrCost = this.allRows.reduce((sum, row) => sum + row.total_ocr_cost, 0);
    this.topMetrics.successRate =
      totalRequests > 0 ? ((totalRequests - totalFailures) / totalRequests) * 100 : 0;
  }

  private applyLocalFiltersAndSort(): void {
    const query = this.searchTerm.trim().toLowerCase();

    let filtered = this.allRows.filter((row) => {
      if (!query) return true;

      const normalizedModel = row.document_model.toLowerCase();
      return normalizedModel.includes(query) || this.isFuzzyMatch(query, normalizedModel);
    });

    if (!this.sortColumn) {
      this.displayedRows = filtered;
      return;
    }

    filtered = filtered.sort((a, b) => {
      const left = a[this.sortColumn!];
      const right = b[this.sortColumn!];

      if (left === right) return 0;
      const comparison = left > right ? 1 : -1;
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.displayedRows = filtered;
  }

  private isFuzzyMatch(search: string, target: string): boolean {
    if (!search) return true;

    let s = 0;
    for (let t = 0; t < target.length && s < search.length; t++) {
      if (target[t] === search[s]) {
        s++;
      }
    }

    return s === search.length;
  }

  private formatDateTimeForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  private buildLocalDateTime(datePart: string, hour12: string, minute: string, period: 'AM' | 'PM'): string {
    const monthDayYear = this.convertDisplayToLocalDateTime(`${this.toDisplayDate(datePart)} ${hour12}:${minute} ${period}`);
    if (monthDayYear) {
      return monthDayYear;
    }

    const hour24 = period === 'PM' ? (Number(hour12) % 12) + 12 : Number(hour12) % 12;
    return `${datePart}T${String(hour24).padStart(2, '0')}:${minute}`;
  }

  private toDisplayDate(datePart: string): string {
    const parts = datePart.split('-');
    if (parts.length !== 3) {
      return datePart;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  private formatDatePartFromDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseLocalDateTime(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);

    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDateTimeForDisplay(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    const minutes = String(date.getMinutes()).padStart(2, '0');
    const isPm = date.getHours() >= 12;
    const hour12 = date.getHours() % 12 || 12;
    const period = isPm ? 'PM' : 'AM';

    return `${month}/${day}/${year} ${String(hour12).padStart(2, '0')}:${minutes} ${period}`;
  }

  private syncTimePartsFromDateTimes(): void {
    const start = this.parseLocalDateTime(this.startDate);
    if (start) {
      const startIsPm = start.getHours() >= 12;
      this.startPeriod = startIsPm ? 'PM' : 'AM';
      this.startHour12 = String(start.getHours() % 12 || 12).padStart(2, '0');
      this.startMinute = String(start.getMinutes()).padStart(2, '0');
    }

    const end = this.parseLocalDateTime(this.endDate);
    if (end) {
      const endIsPm = end.getHours() >= 12;
      this.endPeriod = endIsPm ? 'PM' : 'AM';
      this.endHour12 = String(end.getHours() % 12 || 12).padStart(2, '0');
      this.endMinute = String(end.getMinutes()).padStart(2, '0');
    }
  }

  private toUtcPayloadValue(localDateTime: string): string {
    const localDate = this.parseLocalDateTime(localDateTime);
    if (!localDate) {
      return '';
    }

    return localDate.toISOString();
  }

  private clearReportData(): void {
    this.allRows = [];
    this.displayedRows = [];
    this.updateTopMetrics();
  }

  private updateEndDateInvalidState(): void {
    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    const now = new Date();

    if (!start || !end) {
      this.endDateInvalid = false;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return;
    }

    this.endDateInvalid = end.getTime() > now.getTime();
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
  }

  private validateDateRange(): boolean {
    if (!this.startDate || !this.endDate) {
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      return false;
    }

    const start = this.parseLocalDateTime(this.startDate);
    const end = this.parseLocalDateTime(this.endDate);
    if (!start || !end) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      this.notificationService.showError('Invalid datetime range. Please use valid date and time values.');
      return false;
    }

    if (end.getTime() < start.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = 'End date/time must be greater than start date/time.';
      if (!this.hasShownInvalidDateRangeToast) {
        this.notificationService.showError('End date/time must be greater than start date/time.');
        this.hasShownInvalidDateRangeToast = true;
      }
      return false;
    }

    const now = new Date();
    if (end.getTime() > now.getTime()) {
      this.endDateInvalid = true;
      this.dateRangeValidationMessage = '';
      this.hasShownInvalidDateRangeToast = false;
      this.notificationService.showError('Invalid date picked. End datetime cannot be greater than current datetime.');
      return false;
    }

    this.endDateInvalid = false;
    this.dateRangeValidationMessage = '';
    this.hasShownInvalidDateRangeToast = false;
    return true;
  }


}

